import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ClockIcon,
  MapPinIcon,
  CurrencyYenIcon,
  UserGroupIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';
import { apiWithAuth } from '../../lib/api';

const API = `${API_BASE}`;

const EmployerJobManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // 新規求人作成用の状態
  const [newJobData, setNewJobData] = useState({
    title: '',
    description: '',
    category: '',
    work_date: '',
    work_time_start: '',
    work_time_end: '',
    hourly_rate: '',
    transportation_fee: '',
    prefecture: '',
    city: '',
    address: '',
    required_skills: '',
    required_people: 1,
    is_urgent: false,
    is_published: true,
    job_type: 'single' // single or multiple
  });

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }

    fetchJobs();
  }, [user, navigate]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      if (!token) return;

      const client = apiWithAuth(token);
      const res = await client.get('/employer/recruitments');

      if (res.data) {
        setJobs(res.data);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      toast.error('求人データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // バリデーション
      if (!newJobData.title || !newJobData.work_date || !newJobData.hourly_rate) {
        toast.error('必須項目を入力してください');
        return;
      }

      // 新規求人作成
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const client = apiWithAuth(token);

      const res = await client.post('/employer/jobs', newJobData);

      setJobs(prev => [res.data, ...prev]);

      // フォームリセット
      setNewJobData({
        title: '',
        description: '',
        category: '',
        work_date: '',
        work_time_start: '',
        work_time_end: '',
        hourly_rate: '',
        transportation_fee: '',
        prefecture: '',
        city: '',
        address: '',
        required_skills: '',
        required_people: 1,
        is_urgent: false,
        is_published: true,
        job_type: 'single'
      });

      toast.success('求人を作成しました');
      setActiveTab('list');
    } catch (err) {
      console.error('Error creating job:', err);
      toast.error('求人の作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (jobId) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const client = apiWithAuth(token);
      await client.put(`/employer/jobs/${jobId}/publish`);

      const updatedJobs = jobs.map(job =>
        job.id === jobId
          ? { ...job, is_published: !job.is_published }
          : job
      );
      setJobs(updatedJobs);

      const job = jobs.find(j => j.id === jobId);
      toast.success(`求人を${job.is_published ? '非公開' : '公開'}にしました`);
    } catch (err) {
      toast.error('公開状態の変更に失敗しました');
    }
  };

  const handleToggleUrgent = async (jobId) => {
    try {
      const updatedJobs = jobs.map(job =>
        job.id === jobId
          ? { ...job, is_urgent: !job.is_urgent }
          : job
      );
      setJobs(updatedJobs);

      const job = jobs.find(j => j.id === jobId);
      toast.success(`緊急求人を${job.is_urgent ? '解除' : '設定'}しました`);
    } catch (err) {
      toast.error('緊急求人設定の変更に失敗しました');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('この求人を削除しますか？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const client = apiWithAuth(token);
      await client.delete(`/employer/jobs/${jobId}`);

      const updatedJobs = jobs.filter(job => job.id !== jobId);
      setJobs(updatedJobs);
      toast.success('求人を削除しました');
    } catch (err) {
      toast.error('求人の削除に失敗しました');
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'published' && job.is_published) ||
      (statusFilter === 'draft' && !job.is_published) ||
      (statusFilter === 'urgent' && job.is_urgent);

    return matchesSearch && matchesStatus;
  });

  const renderJobList = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900">求人一覧</h3>
          <div className="mt-3 sm:mt-0 flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="求人を検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">全ての求人</option>
              <option value="published">公開中</option>
              <option value="draft">下書き</option>
              <option value="urgent">緊急求人</option>
            </select>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {filteredJobs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm || statusFilter !== 'all' ? '検索条件に一致する求人がありません' : '求人がありません'}
          </div>
        ) : (
          filteredJobs.map((job) => (
            <div key={job.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h4 className="text-lg font-medium text-gray-900 mr-3">{job.title}</h4>
                    {job.is_urgent && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <BellIcon className="w-3 h-3 mr-1" />
                        緊急
                      </span>
                    )}
                    <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${job.is_published
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                      }`}>
                      {job.is_published ? '公開中' : '下書き'}
                    </span>
                  </div>

                  <p className="text-gray-600 mb-3">{job.description}</p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center text-gray-500">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      {job.work_date}
                    </div>
                    <div className="flex items-center text-gray-500">
                      <CurrencyYenIcon className="w-4 h-4 mr-1" />
                      {job.hourly_rate}/時間
                    </div>
                    <div className="flex items-center text-gray-500">
                      <MapPinIcon className="w-4 h-4 mr-1" />
                      {job.prefecture} {job.city}
                    </div>
                    <div className="flex items-center text-gray-500">
                      <UserGroupIcon className="w-4 h-4 mr-1" />
                      {job.applied_count}/{job.required_people}名
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 ml-6">
                  <button
                    onClick={() => navigate(`/employer/job/${job.id}`)}
                    className="flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    確認
                  </button>
                  <button
                    onClick={() => handleTogglePublish(job.id)}
                    className={`flex items-center px-3 py-1 text-xs font-medium rounded-lg transition-colors ${job.is_published
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                  >
                    {job.is_published ? <EyeSlashIcon className="w-4 h-4 mr-1" /> : <EyeIcon className="w-4 h-4 mr-1" />}
                    {job.is_published ? '非公開' : '公開'}
                  </button>

                  <button
                    onClick={() => handleToggleUrgent(job.id)}
                    className={`flex items-center px-3 py-1 text-xs font-medium rounded-lg transition-colors ${job.is_urgent
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      }`}
                  >
                    <BellIcon className="w-4 h-4 mr-1" />
                    {job.is_urgent ? '緊急解除' : '緊急求人'}
                  </button>

                  <button
                    onClick={() => navigate(`/employer/job-edit/${job.id}`)}
                    className="flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    編集
                  </button>

                  <button
                    onClick={() => handleDeleteJob(job.id)}
                    className="flex items-center px-3 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    削除
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderCreateJobForm = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">新規求人作成</h3>

      <form onSubmit={handleCreateJob} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            求人タイトル <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newJobData.title}
            onChange={(e) => setNewJobData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="例：カフェスタッフ募集"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            勤務内容
          </label>
          <textarea
            value={newJobData.description}
            onChange={(e) => setNewJobData(prev => ({ ...prev, description: e.target.value }))}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="勤務内容を詳しく説明してください"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              カテゴリ
            </label>
            <select
              value={newJobData.category}
              onChange={(e) => setNewJobData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">カテゴリを選択</option>
              <option value="飲食・フード">飲食・フード</option>
              <option value="販売・接客">販売・接客</option>
              <option value="軽作業">軽作業</option>
              <option value="清掃・整備">清掃・整備</option>
              <option value="配達・運転">配達・運転</option>
              <option value="事務・データ入力">事務・データ入力</option>
              <option value="イベント">イベント</option>
              <option value="その他">その他</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              勤務日 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={newJobData.work_date}
              onChange={(e) => setNewJobData(prev => ({ ...prev, work_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              開始時間
            </label>
            <input
              type="time"
              value={newJobData.work_time_start}
              onChange={(e) => setNewJobData(prev => ({ ...prev, work_time_start: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              終了時間
            </label>
            <input
              type="time"
              value={newJobData.work_time_end}
              onChange={(e) => setNewJobData(prev => ({ ...prev, work_time_end: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              時給 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={newJobData.hourly_rate}
              onChange={(e) => setNewJobData(prev => ({ ...prev, hourly_rate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="1000"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              交通費
            </label>
            <input
              type="number"
              value={newJobData.transportation_fee}
              onChange={(e) => setNewJobData(prev => ({ ...prev, transportation_fee: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              募集人数
            </label>
            <input
              type="number"
              value={newJobData.required_people}
              onChange={(e) => setNewJobData(prev => ({ ...prev, required_people: parseInt(e.target.value) || 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="1"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              都道府県
            </label>
            <input
              type="text"
              value={newJobData.prefecture}
              onChange={(e) => setNewJobData(prev => ({ ...prev, prefecture: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="東京都"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              市区町村
            </label>
            <input
              type="text"
              value={newJobData.city}
              onChange={(e) => setNewJobData(prev => ({ ...prev, city: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="渋谷区"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              詳細住所
            </label>
            <input
              type="text"
              value={newJobData.address}
              onChange={(e) => setNewJobData(prev => ({ ...prev, address: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="渋谷1-1-1"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            必要スキル・条件
          </label>
          <textarea
            value={newJobData.required_skills}
            onChange={(e) => setNewJobData(prev => ({ ...prev, required_skills: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="必要なスキルや経験、条件などを記入してください"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={newJobData.is_urgent}
              onChange={(e) => setNewJobData(prev => ({ ...prev, is_urgent: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">緊急求人として設定</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={newJobData.is_published}
              onChange={(e) => setNewJobData(prev => ({ ...prev, is_published: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">すぐに公開する</span>
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => setActiveTab('list')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? '作成中...' : '求人を作成'}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/employer-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            事業者ダッシュボードに戻る
          </button>
          <h1 className="text-3xl font-bold text-gray-900">求人案件の登録・編集</h1>
          <p className="text-gray-600 mt-2">新規求人の作成と既存求人の管理を行います</p>
        </div>

        {/* タブナビゲーション */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('list')}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <ClipboardDocumentListIcon className="w-5 h-5 mr-2" />
                求人一覧
              </button>
              <button
                onClick={() => setActiveTab('create')}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                新規作成
              </button>
            </nav>
          </div>
        </div>

        {/* タブコンテンツ */}
        {activeTab === 'list' ? renderJobList() : renderCreateJobForm()}
      </div>
    </div>
  );
};

export default EmployerJobManagement;